import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { LoginRequestDto, LoginResponseDto, SelectAccountRequestDto } from '../../domain/contracts/api.contracts';
import { postLogin, postLogout, postSelectAccount } from '../../infrastructure/api/authApi';
import { refreshOnce } from '../../infrastructure/api/apiClient';
import { getMe } from '../../infrastructure/api/usersApi';
import { useAuthStore } from '../stores/authStore';
import { qKeys } from './keys';

/** True when E01 answered with the BD-30 account picker instead of a session. */
export function needsAccountSelection(r: LoginResponseDto): r is Extract<LoginResponseDto, { selectionRequired: true }> {
  return 'selectionRequired' in r && r.selectionRequired === true;
}

/**
 * E01 — login: stores the in-memory session (Key Invariant 1) and clears every
 * cached query so no data bleeds between accounts. When the person holds several apartment accounts
 * (BD-30) only the pending selection is stored — there is no session until `useSelectAccount` succeeds.
 */
export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: LoginRequestDto) => postLogin(body),
    onSuccess: (result) => {
      if (needsAccountSelection(result)) {
        useAuthStore.getState().setPendingSelection(result);
        return;
      }
      useAuthStore.getState().setSession(result.accessToken, result.user);
      queryClient.clear();
      void queryClient.invalidateQueries({ queryKey: qKeys.dashboard });
    },
  });
}

/** E27 — choose one apartment account; the session is bound to it until sign-out. */
export function useSelectAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: SelectAccountRequestDto) => postSelectAccount(body),
    onSuccess: (session) => {
      useAuthStore.getState().setSession(session.accessToken, session.user);
      queryClient.clear();
      void queryClient.invalidateQueries({ queryKey: qKeys.dashboard });
    },
  });
}

/**
 * E03 — logout: best-effort server call, then always clear the local session
 * (even if the 204 call fails — the user must land logged out either way).
 */
export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => postLogout(),
    onSettled: () => {
      useAuthStore.getState().clearSession();
      queryClient.clear();
    },
  });
}

let restorePromise: Promise<void> | null = null;

/**
 * Page reload wipes the in-memory access token (Key Invariant 1), but the httpOnly refresh cookie survives.
 * Silently exchange it for a fresh token + profile (E02 → E04) once per page load; on any failure the
 * session stays empty and RequireAuth falls through to /login. Both outcomes flip `restoreAttempted`
 * (via setSession/clearSession). Single-flight so StrictMode's double effects share one attempt.
 */
export function restoreSession(): Promise<void> {
  if (!restorePromise) {
    restorePromise = (async () => {
      try {
        const token = await refreshOnce();
        const user = await getMe();
        useAuthStore.getState().setSession(token, user);
      } catch {
        useAuthStore.getState().clearSession();
      } finally {
        restorePromise = null;
      }
    })();
  }
  return restorePromise;
}
